// ─── Supabase client state ────────────────────────────────────────────────────
// These are set at runtime from env vars or user-entered values in Settings.
// The anon key is safe to expose client-side — Supabase RLS enforces security.
export let SB_URL = import.meta.env.VITE_SUPABASE_URL || '';
export let SB_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
export let SB_TOKEN = ''; // JWT set after sign-in; replaces anon key for authed requests

export function setSbUrl(url) { SB_URL = url; }
export function setSbKey(key) { SB_KEY = key; }
export function setSbToken(token) { SB_TOKEN = token; }

export function sbHeaders(extra = {}) {
  return {
    apikey: SB_KEY,
    Authorization: `Bearer ${SB_TOKEN || SB_KEY}`,
    'Content-Type': 'application/json',
    ...extra,
  };
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function sbSignUp(email, password) {
  const res = await fetch(`${SB_URL}/auth/v1/signup`, {
    method: 'POST',
    headers: { apikey: SB_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || data.error_description || 'Sign up failed');
  return data;
}

export async function sbSignIn(email, password) {
  const res = await fetch(`${SB_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: SB_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error_description || data.msg || 'Sign in failed');
  return data; // { access_token, refresh_token, expires_in, user }
}

export async function sbSignOut(token) {
  await fetch(`${SB_URL}/auth/v1/logout`, {
    method: 'POST',
    headers: { apikey: SB_KEY, Authorization: `Bearer ${token}` },
  });
}

export async function sbRefreshSession(refreshToken) {
  const res = await fetch(`${SB_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: { apikey: SB_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error('Session expired');
  return data;
}

export function sbSignInWithGoogle() {
  const redirectTo = encodeURIComponent(window.location.origin);
  window.location.href = `${SB_URL}/auth/v1/authorize?provider=google&redirect_to=${redirectTo}`;
}

export async function sbResetPassword(email) {
  const res = await fetch(`${SB_URL}/auth/v1/recover`, {
    method: 'POST',
    headers: { apikey: SB_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Password reset failed');
}

// ── Profiles ──────────────────────────────────────────────────────────────────

export async function sbGetProfile(userId, token, key) {
  const res = await fetch(`${SB_URL}/rest/v1/profiles?user_id=eq.${userId}&select=*`, {
    headers: { apikey: key, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.[0] || null;
}

export async function sbSaveProfile(profile, token) {
  const res = await fetch(`${SB_URL}/rest/v1/profiles`, {
    method: 'POST',
    headers: {
      apikey: SB_KEY,
      Authorization: `Bearer ${token || SB_TOKEN || SB_KEY}`,
      'Content-Type': 'application/json',
      Prefer: 'resolution=merge-duplicates,return=representation',
    },
    body: JSON.stringify(profile),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function sbCheckUsername(username) {
  const res = await fetch(
    `${SB_URL}/rest/v1/profiles?username=eq.${encodeURIComponent(username)}&select=username`,
    { headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}` } },
  );
  if (!res.ok) return false;
  const data = await res.json();
  return data.length > 0; // true = already taken
}

export async function sbDeleteAccount(userId, token, key) {
  await Promise.all([
    fetch(`${SB_URL}/rest/v1/recipes?user_id=eq.${userId}`, {
      method: 'DELETE',
      headers: { apikey: key, Authorization: `Bearer ${token}`, Prefer: 'return=minimal' },
    }),
    fetch(`${SB_URL}/rest/v1/meal_plan?user_id=eq.${userId}`, {
      method: 'DELETE',
      headers: { apikey: key, Authorization: `Bearer ${token}`, Prefer: 'return=minimal' },
    }),
    fetch(`${SB_URL}/rest/v1/cupboard?user_id=eq.${userId}`, {
      method: 'DELETE',
      headers: { apikey: key, Authorization: `Bearer ${token}`, Prefer: 'return=minimal' },
    }),
    fetch(`${SB_URL}/rest/v1/profiles?user_id=eq.${userId}`, {
      method: 'DELETE',
      headers: { apikey: key, Authorization: `Bearer ${token}`, Prefer: 'return=minimal' },
    }),
  ]);
  await fetch(`${SB_URL}/auth/v1/user`, {
    method: 'DELETE',
    headers: { apikey: key, Authorization: `Bearer ${token}` },
  });
}

// ── Generic data helpers ──────────────────────────────────────────────────────

export async function sbGet(table, filter = '') {
  const res = await fetch(`${SB_URL}/rest/v1/${table}?select=*${filter}`, {
    headers: sbHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function sbUpsert(table, row) {
  const res = await fetch(`${SB_URL}/rest/v1/${table}`, {
    method: 'POST',
    headers: sbHeaders({ Prefer: 'resolution=merge-duplicates,return=representation' }),
    body: JSON.stringify(row),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json().catch(() => null);
}

export async function sbDelete(table, match) {
  const params = Object.entries(match)
    .map(([k, v]) => `${k}=eq.${encodeURIComponent(v)}`)
    .join('&');
  const res = await fetch(`${SB_URL}/rest/v1/${table}?${params}`, {
    method: 'DELETE',
    headers: sbHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function sbUploadPhoto(path, file) {
  const res = await fetch(`${SB_URL}/storage/v1/object/recipe-images/${path}`, {
    method: 'POST',
    headers: {
      apikey: SB_KEY,
      Authorization: `Bearer ${SB_TOKEN || SB_KEY}`,
      'Content-Type': file.type,
    },
    body: file,
  });
  if (!res.ok) throw new Error(await res.text());
  return `${SB_URL}/storage/v1/object/public/recipe-images/${path}`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Parse user ID from a Supabase JWT without a library. */
export function parseUidFromToken(token) {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1])).sub;
  } catch {
    return null;
  }
}
